import { useState } from 'react';
import {
  Button,
  Card,
  Field,
  Flex,
  Grid,
  GridItem,
  Input,
  Text,
} from '@chakra-ui/react';
import CpfInput from '@components/CpfInput';
import PhoneInput from '@components/PhoneInput';
import { datadogRum } from '@datadog/browser-rum';
import { formatPhoneNumber, onlyDigits } from '@utils/formats';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router';
import { PHONE_NUMBER } from '@utils/regex';
import { useMyDispositionsContext } from '../context';

const Filters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultName = searchParams.get('name') || '';
  const defaultPhoneNumber = searchParams.get('phoneNumber') || '';
  const defaultEmail = searchParams.get('email') || '';
  const defaultTaxId = searchParams.get('taxId') || '';

  const [loading, setLoading] = useState(false);

  const { setShowFilters, getMyDispositionsList } = useMyDispositionsContext();

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();    

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    if (data.phoneNumber && !PHONE_NUMBER.test(data.phoneNumber as string)) {
      toast.error('Número de telefone inválido.');
      return;
    }

    setLoading(true);

    const params = {
      name: data.name as string,
      email: data.email as string,
      taxId: onlyDigits(data.taxId as string),
      phoneNumber: onlyDigits(data.phoneNumber as string),
    };

    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined && v !== '')
    );

    const newParams = new URLSearchParams(filteredParams as { [key: string]: string });

    setSearchParams(newParams);

    try {
      await getMyDispositionsList({
        ...filteredParams,
      });
      setShowFilters(false);
    } catch (error) {
      toast.error('Ocorreu em erro ao aplicar os filtros. Tente novamente!');
      datadogRum.addError(new Error('Fail to apply dispositions filters'), {
        error,
        params: filteredParams,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      onSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
    }
  };

  return (
    <Card.Root
      size="sm"
      variant="subtle"
      bg="grayscale.fg"
      my={4}
      p={4}
      display="flex"
      justifyContent="center"
      cursor="pointer"
    >
      <Text fontWeight={500} mb={3}>
        Filtros
      </Text>
      <form onSubmit={onSubmit}>
        <Grid templateColumns="repeat(4, 1fr)" gap="2">
          <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
            <Field.Root>
              <Field.Label>Nome</Field.Label>
              <Input
                name="name"
                placeholder="Pesquisar por nome"
                variant="subtle"
                defaultValue={defaultName}
                onKeyDown={handleKeyDown}
              />
            </Field.Root>
          </GridItem>
          <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
            <Text fontSize="sm" mb={1} fontWeight={500}>
              Telefone
            </Text>
            <PhoneInput
              name="phoneNumber"
              placeholder="Pesquisar pelo telefone"
              variant="subtle"
              defaultValue={formatPhoneNumber(onlyDigits(defaultPhoneNumber))}
              onKeyDown={handleKeyDown}
            />
          </GridItem>
          <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
            <Field.Root>
              <Field.Label>E-mail</Field.Label>
              <Input
                name="email"
                placeholder="Pesquisar pelo e-mail"
                variant="subtle"
                defaultValue={defaultEmail}
                onKeyDown={handleKeyDown}
              />
            </Field.Root>
          </GridItem>
          <GridItem colSpan={{ base: 4, md: 2, lg: 1 }}>
            <Field.Root>
              <Field.Label>CPF</Field.Label>
                <CpfInput
                  name="taxId"
                  placeholder="Pesquisar pelo CPF"
                  variant="subtle"
                  defaultValue={defaultTaxId}
                  onKeyDown={handleKeyDown}
                />
            </Field.Root>
          </GridItem>
        </Grid>
        <Flex justifyContent="flex-end" mt={4} gap={1}>
          <Button size="sm" variant="ghost" loading={loading} onClick={() => setShowFilters(false)}>
            Fechar
          </Button>
          <Button size="sm" type="submit" loading={loading}>
            Aplicar
          </Button>
        </Flex>
      </form>
    </Card.Root>
  );
};

export default Filters;
